import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanLoad, Router } from '@angular/router';

import { LinkTarget } from '../shared/constants/link-target.enum';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FileType } from '../shared/models/file-item.model';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { Template } from '../shared/templates/templates.model';
import { CreateDocumentResult, CreateDocumentResultType, CreateDocumentService } from './../shared/services/create-document.service';
import { BaseRouteGuard } from './base.route-guard';

@Injectable({
    providedIn: 'root'
})
export class ModernStartRouteGuard extends BaseRouteGuard implements CanActivate, CanLoad {
    private static readonly CREATE_NEW_REDIRECTS = {
        'word.new': FileType.Word,
        'templates.word.new': FileType.Word,
        'powerpoint.new': FileType.PowerPoint,
        'templates.powerpoint.new': FileType.PowerPoint,
        'excel.new': FileType.Excel,
        'templates.excel.new': FileType.Excel,
        'sway.new': FileType.Sway
        // Todo: For Onenote, a dialog box will open and request for the name of the file because opening
        // Problematic because we need scenarios where the link takes you to a new file and not a dialog box.
        // If we can name the new onenote uniquely or query for existing names, it might be possible to decide names for them
        // on client side.
        //'onenote.new' : FileType.OneNote
    };
    private readonly startEnabled: boolean;

    constructor(
        router: Router,
        capabilitiesService: CapabilitiesService,
        public readonly createDocumentService: CreateDocumentService,
        readonly instrumentationService: InstrumentationService
    ) {
        super(router, capabilitiesService);
        this.startEnabled = capabilitiesService.capabilities.modernStartPageEnabled;
    }

    public canActivate(route: ActivatedRouteSnapshot) {
        const msUrl = route.queryParams['ms.url'];
        if (msUrl) {
            return this.checkAndNavigateToCreateNew(msUrl, route.queryParams['templateId']);
        } else {
            return this.checkEnabledAndRedirect(this.startEnabled);
        }
    }

    public canLoad() {
        return this.checkEnabledAndRedirect(this.startEnabled);
    }

    public checkAndNavigateToCreateNew(msUrlParam: string, templateId: string): Promise<boolean> {
        msUrlParam = msUrlParam.toLowerCase();
        return new Promise(resolve => {
            const fileType: FileType = ModernStartRouteGuard.CREATE_NEW_REDIRECTS[msUrlParam];
            if (fileType) {
                let template: Template | undefined;
                if (msUrlParam.lastIndexOf('templates.') === 0 && templateId) {
                    template = { AssetId: templateId } as Template;
                }

                // resolves true to navigate to the startPage as a fallback because of failure to create file.
                // resolves false to not navigate to the start page as a fallback because of success to create the file. Instead navigate to the actual create file.
                const resultHandler = (result: CreateDocumentResult) => {
                    const params = {
                            [InstrumentationParam.MsNewParam]: msUrlParam,
                            [InstrumentationParam.Action]: result && result.type || ''
                    };

                    if (result && result.type !== CreateDocumentResultType.Error && result.type !== CreateDocumentResultType.CreatePending) {
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.CreateNewRedirectSuccess, params);
                        resolve(false);
                    } else {
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.CreateNewRedirectFail, params);
                        resolve(true);
                    }
                };
                this.createDocumentService.createItem(fileType, null, template, resultHandler, null, null, LinkTarget.SameFrame);
            }
        });
    }
}
